import { ReactComponent as NotificationsIcon } from "assets/icons/notifications.svg"
import { ReactComponent as SettingsIcon } from "assets/icons/settings.svg"
import { ReactComponent as BackIcon } from "assets/icons/back.svg"

import { useEffect, useMemo, useRef, useState } from "react"
import Notification from "./notification-details"
import { Wrapper, Header, Content, EmptyList } from "./styles"
import styles from "./styles.module.scss"
import classNames from "classnames"
import { useNavigate } from "react-router-dom"
import { echo } from "services/laravel-echo"
import {
  useMarkAllAsRead,
  useMarkAllAsUnread,
  useNotifications,
  useUnreadNotifications,
} from "./queries"
import { useAuth } from "providers/auth"
import { useTranslation } from "react-i18next"
import { Badge, Popover } from "antd"
import { NotificationSkeleton } from "./notification-skeleton"
import CircleButton from "@atoms/icon-button"

const Notifications = () => {
  const { t } = useTranslation()
  const [open, setOpen] = useState<boolean>(false)
  const [lastElement, setLastElement] = useState(null)
  const navigate = useNavigate()
  const { user } = useAuth()

  const [unreadOnly, displayUnreadOnly] = useState<boolean>(false)

  const notificationsList = useNotifications({
    enabled: open && !unreadOnly,
  })

  const unreadNotificationsList = useUnreadNotifications({
    enabled: open && unreadOnly,
  })

  const notifications = useMemo(() => {
    if (unreadOnly)
      return unreadNotificationsList.data?.pages.flatMap(
        (page: any) => page.data
      )
    else return notificationsList.data?.pages.flatMap((page: any) => page.data)
  }, [unreadNotificationsList.data, notificationsList.data, unreadOnly])

  const isLoading = useMemo(() => {
    return unreadOnly
      ? unreadNotificationsList.isLoading
      : notificationsList.isLoading
  }, [
    unreadNotificationsList.isLoading,
    notificationsList.isLoading,
    unreadOnly,
  ])

  const total_unread = useMemo(() => {
    return notificationsList.data?.pages[0].total_unread
  }, [notificationsList.data])

  const hasNextPage = useMemo(() => {
    return unreadOnly
      ? unreadNotificationsList.hasNextPage
      : notificationsList.hasNextPage
  }, [
    unreadOnly,
    notificationsList.hasNextPage,
    unreadNotificationsList.hasNextPage,
  ])

  const { mutate: markAllAsRead } = useMarkAllAsRead()
  const { mutate: markAllAsUnread } = useMarkAllAsUnread()

  const allObserver = useRef(
    new IntersectionObserver((entries) => {
      const firstEntry = entries[0]
      if (firstEntry.isIntersecting) {
        notificationsList.fetchNextPage()
      }
    })
  )

  const unreadObserver = useRef(
    new IntersectionObserver((entries) => {
      const firstEntry = entries[0]
      if (firstEntry.isIntersecting) {
        unreadNotificationsList.fetchNextPage()
      }
    })
  )

  useEffect(() => {
    const currentElement = lastElement
    const currentObserver = unreadOnly
      ? unreadObserver.current
      : allObserver.current

    if (currentElement) {
      currentObserver.observe(currentElement)
    }

    return () => {
      if (currentElement) {
        currentObserver.unobserve(currentElement)
      }
    }
  }, [unreadOnly, lastElement])

  useEffect(() => {
    echo.private(`Portal.User.Notification.${user?.id}`).notification(() => {
      notificationsList.refetch()
      unreadNotificationsList.refetch()
    })
  }, [])

  useEffect(() => {
    unreadOnly ? unreadNotificationsList.refetch() : notificationsList.refetch()
  }, [unreadOnly])

  return (
    <Popover
      onOpenChange={setOpen}
      open={open}
      trigger={["click"]}
      content={
        <Wrapper>
          <Header>
            <span className="subheader-heavy" style={{ display: "flex" }}>
              <div className={styles.closeNotificationsBtn}>
                <CircleButton
                  style={{ marginRight: "4px" }}
                  icon={<BackIcon />}
                  size="small"
                  type="dashed"
                  onClick={() => setOpen(false)}
                />
              </div>
              {t("notifications.title")}
            </span>
            {!total_unread ? (
              <span className="caption-heavy" onClick={() => markAllAsUnread()}>
                {t("notifications.mark_all_unread")}
              </span>
            ) : (
              <span className="caption-heavy" onClick={() => markAllAsRead()}>
                {t("notifications.mark_all_read")}
              </span>
            )}
          </Header>
          <Content>
            <div className={styles.navigation}>
              <div
                onClick={() => displayUnreadOnly(false)}
                className={classNames(styles.navItem, "body-heavy", {
                  [styles["navItem--active"]]: !unreadOnly,
                })}
              >
                {t("all")}
              </div>
              <div
                onClick={() => displayUnreadOnly(true)}
                className={classNames(styles.navItem, "body-heavy", {
                  [styles["navItem--active"]]: unreadOnly,
                })}
              >
                {t("notifications.unread")}
              </div>
              <span
                className={styles.settings}
                onClick={() => navigate("/user/profile/notifications")}
              >
                <SettingsIcon width={20} />
              </span>
            </div>

            <div className={styles.notificationsWrapper}>
              <div>
                {notifications?.map((notif, index) => (
                  <Notification key={index} {...notif} />
                ))}
                {hasNextPage && <NotificationSkeleton ref={setLastElement} />}
                {isLoading && <NotificationSkeleton />}
                {notifications?.length === 0 && !isLoading && (
                  <EmptyList>
                    <NotificationsIcon width={64} />
                    <span>{t("notifications.noFound")}</span>
                  </EmptyList>
                )}
              </div>
            </div>
          </Content>
        </Wrapper>
      }
    >
      <Badge count={total_unread} className={styles.notificationsTrigger}>
        <NotificationsIcon width={22} />
      </Badge>
    </Popover>
  )
}

export default Notifications
